// screen: Players — player configurations (change players-config-editor; Studio spec §4.9) + preview + embed code
// Two panes (studio change settings-ux, 2026-09-29): the settings on one side and a sticky live Player v2 preview on the other, so every change is
// visible without scrolling (RTL: settings on the right, preview on the left; narrow screens: preview sticky on top).
// Simple / Advanced views; the preview can play a library video, the live channel or catch-up, at desktop or phone width.
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Bug, Copy, ExternalLink, Monitor, Plus, Smartphone, Star, Trash2, Tv, Undo2 } from 'lucide-react';
import type { ApiError } from '@viewstream/api-client';
import { Badge, Button, ConfirmByNameDialog, EmptyState, ErrorState, Field, Input, Select, Skeleton, Switch, useToast } from '@viewstream/ui';
import { PreviewFrame, type PlayerLook } from '@viewstream/player-react';
import { fmtDateTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { useAssetPages } from '~/app/assets';
import { useChannels, useClips } from '~/app/live';
import { buildEmbed, type EmbedKind } from '~/app/embed';
import { EmbedShare } from '~/app/EmbedShare';
import { can } from '~/app/permissions';
import { PlayerConfigEditor } from '~/app/PlayerConfigEditor';
import { PresetAssignments } from '~/app/PresetAssignments';
import { TvAppsSettings } from '~/app/TvAppsSettings';
import { TenantPackaging, packagingLabel } from '~/app/Packaging';
import { usePackaging, type PackagingScope } from '~/app/packaging';
import { continuousUrl } from '~/app/programmeVod';
import { ModeToggle, useUiMode } from '~/app/uiMode';
import { useTVLab } from '~/app/tvlab';
import {
  changedPaths, fieldErrorsOf, lookOf, structuralKey, useCreatePlayerConfig, useDeletePlayerConfig, usePlayerConfigs, useSavePlayerConfig,
  type PlayerConfigDoc, type PlayerConfigView,
} from '~/app/playerConfigs';

export const Route = createFileRoute('/_layout/t/$slug/players')({ component: Screen });

type Kind = 'vod' | 'channel' | 'catchup';
type Width = 'desktop' | 'mobile';

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  React.useEffect(() => { document.title = appTitle(t`Players`); }, [t]);
  const canWrite = can(scopes, 'delivery:write');
  const list = usePlayerConfigs(tenant.slug);
  const [selId, setSelId] = React.useState<string | null>(null);
  const items = list.data?.items ?? [];
  const sel = items.find((c) => c.id === selId) ?? items.find((c) => c.is_default) ?? items[0];
  const tvlab = useTVLab(tenant.slug, false); // TV Preview: the link shows only where it is open (operators, flag tvlab.preview)

  if (list.isError) return <ErrorState error={list.error as ApiError} onRetry={() => list.refetch()} labels={{ title: t`Could not load the player settings`, retry: t`Try again`, requestId: t`Request id`, copy: t`Copy` }} />;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold">{t`Players`}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t`How the video player looks and behaves on your sites and apps. The preview shows every change right away; viewers get it after you save.`}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {tvlab.data?.available ? (
            <Link to={'/t/$slug/players/tv' as never} params={{ slug: tenant.slug } as never} className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline" data-testid="players-tv-link">
              <Tv className="size-4" aria-hidden /> {t`TV Preview`}
            </Link>
          ) : null}
          {can(scopes, 'stats:read') ? (
            <Link to={'/t/$slug/players/crashes' as never} params={{ slug: tenant.slug } as never} className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline" data-testid="players-crashes-link">
              <Bug className="size-4" aria-hidden /> {t`App crashes`}
            </Link>
          ) : null}
          <ModeToggle />
        </div>
      </div>
      {list.isPending ? <Skeleton className="h-96" /> : sel ? (
        <ConfigPanel key={sel.id} view={sel} items={items} onSelect={setSelId} canWrite={canWrite} tenant={tenant.slug} onDeleted={() => setSelId(null)} />
      ) : (
        <EmptyState title={t`No player settings yet`} description={t`The platform creates the default player settings with the account. If this stays empty, contact Interhost support.`} />
      )}
      {items.length ? <TvAppsSettings tenant={tenant.slug} items={items} canWrite={canWrite} onOpen={(id) => { setSelId(id); window.scrollTo({ top: 0, behavior: 'smooth' }); }} /> : null}
      <PresetAssignments tenant={tenant.slug} canWrite={canWrite} />
    </div>
  );
}

function NewConfigForm({ from, tenant, onCreated, onCancel }: { from?: string; tenant: string; onCreated: (id: string) => void; onCancel: () => void }) {
  const { t } = useLingui();
  const toast = useToast();
  const create = useCreatePlayerConfig(tenant);
  const [name, setName] = React.useState('');
  const [err, setErr] = React.useState<string | undefined>();
  // change tenant-settings-sections: a new config can start from the tenant branding (Settings → Branding)
  const [base, setBase] = React.useState<'copy' | 'brand'>('copy');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(undefined);
    create.mutate({ name: name.trim(), from: base === 'brand' ? undefined : from }, {
      onSuccess: (v) => { setName(''); onCreated(v.id); toast.push({ title: t`Player settings created`, tone: 'ok' }); },
      onError: (e2) => setErr(fieldErrorsOf(e2)['name'] ?? (e2 as ApiError).problem?.detail ?? t`Could not create the player settings. Try again.`),
    });
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-surface-2 p-3" data-testid="pcfg-new-form">
      <div className="min-w-56 flex-1">
        <Field id="pcfg-new" label={t`Name of the new settings`} hint={t`Lowercase English letters, digits and dashes, e.g. article-embed.`} error={err}>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus -- opened by the user's click on "New" */}
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value.toLowerCase())} placeholder="article-embed" className="ltr-isolate" />
        </Field>
      </div>
      <fieldset className="mb-4 space-y-1 text-sm">
        <legend className="sr-only">{t`Start from`}</legend>
        <label className="flex items-center gap-2"><input type="radio" name="pcfg-base" checked={base === 'copy'} onChange={() => setBase('copy')} />{t`A copy of the settings shown now`}</label>
        <label className="flex items-center gap-2"><input type="radio" name="pcfg-base" checked={base === 'brand'} onChange={() => setBase('brand')} data-testid="pcfg-from-brand" />{t`The account branding (colour and logo)`}</label>
      </fieldset>
      <div className="mb-4 flex gap-2">
        <Button type="submit" size="sm" disabled={!name.trim()} loading={create.isPending}>{t`Create`}</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>{t`Cancel`}</Button>
      </div>
    </form>
  );
}

function ConfigPanel({ view, items, onSelect, canWrite, tenant, onDeleted }: { view: PlayerConfigView; items: PlayerConfigView[]; onSelect: (id: string) => void; canWrite: boolean; tenant: string; onDeleted: () => void }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const toast = useToast();
  const [mode] = useUiMode();
  const save = useSavePlayerConfig(tenant);
  const del = useDeletePlayerConfig(tenant);
  const [draft, setDraft] = React.useState<PlayerConfigDoc>(view.config);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [confirm, setConfirm] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  // a newer version from the server replaces the draft when the draft was clean (or equals what we just saved);
  // unsaved edits survive another editor's save
  const base = React.useRef(view.config);
  React.useEffect(() => {
    setDraft((d) => (!changedPaths(d, base.current).length || !changedPaths(d, view.config).length ? view.config : d));
    base.current = view.config;
  }, [view.version]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = changedPaths(view.config, draft);

  const doSave = () => {
    setErrors({});
    save.mutate({ id: view.id, config: draft }, {
      onSuccess: (v) => toast.push({ title: t`Saved (version ${v.version}). Players get the change within a minute.`, tone: 'ok' }),
      onError: (e) => { const fe = fieldErrorsOf(e); setErrors(fe); toast.push({ title: Object.keys(fe).length ? t`Some settings need fixing — they are marked in red` : t`Could not save. Try again.`, tone: 'danger' }); },
    });
  };
  const discard = () => { setDraft(view.config); setErrors({}); };
  const makeDefault = () => save.mutate({ id: view.id, is_default: true }, {
    onSuccess: () => toast.push({ title: t`"${view.name}" is now the default`, tone: 'ok' }),
    onError: () => toast.push({ title: t`Could not change the default. Try again.`, tone: 'danger' }),
  });
  const pick = (id: string) => {
    if (id === view.id) return;
    if (dirty.length && !window.confirm(t`You have unsaved changes. Switch anyway and lose them?`)) return;
    onSelect(id);
  };

  return (
    <div className="space-y-4">
      {/* settings picker + actions */}
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-border bg-surface p-3" data-testid="pcfg-bar">
        <div className="flex min-w-0 flex-wrap items-end gap-3">
          <div>
            <span className="mb-1 block text-sm font-medium" aria-hidden>{t`Player settings`}</span>
            <Select ariaLabel={t`Player settings`} value={view.id} onValueChange={pick} className="min-w-56"
              options={items.map((c) => ({ value: c.id, label: c.is_default ? t`${c.name} (default)` : c.name }))} />
          </div>
          <p className="pb-2 text-xs text-fg-muted">
            {view.is_default ? <Badge tone="primary">{t`Default`}</Badge> : null}{' '}
            {t`Version`} <span className="num">{view.version}</span> · <span className="num">{fmtDateTime(view.updated_at, { locale, tz })}</span>
            {mode === 'advanced' && view.updated_by ? <> · <span className="ltr-isolate">{view.updated_by}</span></> : null}
          </p>
        </div>
        {canWrite ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setCreating((v) => !v)} aria-expanded={creating} data-testid="pcfg-new"><Plus className="size-4" aria-hidden /> {t`New`}</Button>
            {!view.is_default ? <Button variant="secondary" size="sm" onClick={makeDefault} disabled={!!dirty.length}><Star className="size-4" aria-hidden /> {t`Make default`}</Button> : null}
            {!view.is_default ? <Button variant="danger" size="sm" onClick={() => setConfirm(true)}><Trash2 className="size-4" aria-hidden /> {t`Delete`}</Button> : null}
          </div>
        ) : <p className="text-xs text-fg-muted">{t`View only: your role cannot change player settings.`}</p>}
      </div>
      {creating && canWrite ? <NewConfigForm from={view.id} tenant={tenant} onCreated={(id) => { setCreating(false); onSelect(id); }} onCancel={() => setCreating(false)} /> : null}

      {/* two panes: settings | sticky live preview. DOM order = settings first, so RTL puts them on the right. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:items-start" data-testid="pcfg-panes">
        <div className="min-w-0 space-y-3" data-testid="pcfg-controls">
          {mode === 'advanced' ? <PublicLink url={view.public_url} /> : null}
          <PlayerConfigEditor
            value={draft}
            onChange={setDraft}
            errors={errors}
            readOnly={!canWrite}
            mode={mode}
            simpleExtra={
              <section className="rounded-lg border border-border bg-surface p-4" data-testid="pcfg-packaging">
                <TenantPackaging compact idPrefix="pcfg-packaging" />
              </section>
            }
          />
          {canWrite ? (
            <div className={`sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 shadow-pop ${dirty.length ? 'border-warn/50 bg-surface' : 'border-border bg-surface'}`} data-testid="pcfg-savebar">
              <p className="text-sm" role="status">
                {dirty.length ? <span className="text-warn">{t`Unsaved changes: ${dirty.length}`}</span> : <span className="text-fg-muted">{t`All changes saved`}</span>}
              </p>
              <div className="flex gap-2">
                {dirty.length ? <Button variant="ghost" size="sm" onClick={discard}><Undo2 className="size-4 icon-dir" aria-hidden /> {t`Discard`}</Button> : null}
                <Button size="sm" onClick={doSave} disabled={!dirty.length} loading={save.isPending && !!save.variables?.config} data-testid="pcfg-save">{t`Save`}</Button>
              </div>
            </div>
          ) : null}
        </div>
        <Preview config={draft} configName={view.name} version={view.version} />
      </div>

      <EmbedSection config={draft} configName={view.name} />
      <ConfirmByNameDialog
        open={confirm}
        onOpenChange={setConfirm}
        name={view.name}
        title={t`Delete player settings`}
        description={t`Embeds that use them switch to the default settings.`}
        labels={{ field: t`Type the name to confirm`, confirm: t`Delete`, cancel: t`Cancel`, close: t`Close` }}
        busy={del.isPending}
        onConfirm={() => del.mutate(view.id, { onSuccess: () => { setConfirm(false); onDeleted(); toast.push({ title: t`Player settings deleted`, tone: 'ok' }); } })}
      />
    </div>
  );
}

function PublicLink({ url }: { url: string }) {
  const { t } = useLingui();
  const toast = useToast();
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md bg-surface-2 p-2 text-xs">
      <span className="text-fg-muted">{t`Published at`}</span>
      <code className="ltr-isolate break-all" data-testid="pcfg-public-url">{url}</code>
      <Button variant="ghost" size="sm" onClick={() => { void navigator.clipboard?.writeText(url); toast.push({ title: t`Link copied`, tone: 'ok' }); }}><Copy className="size-4" aria-hidden /> {t`Copy`}</Button>
      <a className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline" href={url} target="_blank" rel="noreferrer"><ExternalLink className="size-3.5" aria-hidden /> {t`Open`}</a>
    </div>
  );
}

interface Source { value: string; label: string; src: string; tsSrc?: string; title?: string | null; hosted: { kind: EmbedKind; id: string }; pkg: PackagingScope; channelId?: string }

/** The preview's playable sources: library videos (then clips), live channels, catch-up of the last half hour. */
function useSources(kind: Kind): { options: Source[]; loading: boolean } {
  const { tenant } = useTenant();
  const clips = useClips(tenant.slug);
  const channels = useChannels(tenant.slug);
  const assets = useAssetPages(tenant.slug, { status: 'ready' });
  // catch-up starts 30 minutes back, fixed when the screen opens (a clock-following URL would restart the player)
  const [catchupStart] = React.useState(() => Math.floor((Date.now() - 30 * 60_000) / 60_000) * 60_000);
  const options = React.useMemo<Source[]>(() => {
    if (kind === 'channel') return (channels.data ?? []).filter((c) => c.playback?.live).map((c) => ({ value: c.id!, label: c.title || c.slug!, src: c.playback!.live!, title: c.title, hosted: { kind: 'live' as EmbedKind, id: c.slug! }, pkg: { kind: 'channel', id: c.id! }, channelId: c.id }));
    if (kind === 'catchup') {
      return (channels.data ?? []).flatMap((c) => {
        const src = continuousUrl(c, catchupStart);
        return src ? [{ value: c.id!, label: c.title || c.slug!, src, title: c.title, hosted: { kind: 'live' as EmbedKind, id: c.slug! }, pkg: { kind: 'channel', id: c.id! } as PackagingScope, channelId: c.id }] : [];
      });
    }
    const vids: Source[] = assets.rows.filter((a) => a.id && tenant.cdn_hostname).map((a) => ({ value: a.id!, label: a.title || a.id!, src: `https://${tenant.cdn_hostname}/vod/${tenant.slug}/${a.id}/master.m3u8`, title: a.title, hosted: { kind: 'vod' as EmbedKind, id: a.id! }, pkg: { kind: 'asset', id: a.id! } }));
    const cl: Source[] = (clips.data ?? []).filter((c) => c.playback?.hls && c.status !== 'deleted').map((c) => ({ value: c.id!, label: c.title || c.id!, src: c.playback!.hls!, title: c.title, hosted: { kind: 'clip' as EmbedKind, id: c.id! }, pkg: { kind: 'tenant' } }));
    return [...vids, ...cl];
  }, [kind, clips.data, channels.data, assets.rows, tenant, catchupStart]);
  const loading = kind === 'vod' ? assets.isPending || clips.isPending : channels.isPending;
  return { options, loading };
}

/** Debounces a value (the preview re-creates the player for structural changes; colours apply instantly). */
function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = React.useState(v);
  React.useEffect(() => { const h = window.setTimeout(() => setD(v), ms); return () => window.clearTimeout(h); }, [v, ms]);
  return d;
}

function Preview({ config, configName, version }: { config: PlayerConfigDoc; configName: string; version: number }) {
  const { t } = useLingui();
  const { slug } = useTenant().tenant;
  const [kind, setKind] = React.useState<Kind>('channel');
  const [id, setId] = React.useState('');
  const [width, setWidth] = React.useState<Width>('desktop');
  const [asTs, setAsTs] = React.useState(false);
  const { options, loading } = useSources(kind);
  React.useEffect(() => { if (!options.find((o) => o.value === id)) setId(options[0]?.value ?? ''); }, [options, id]);
  const sel = options.find((o) => o.value === id);
  const pkg = usePackaging(sel?.pkg ?? { kind: 'tenant' });
  const tsSrc = sel && kind === 'channel' ? pkg.data?.urls?.ts?.['live'] : sel && kind === 'vod' && sel.pkg.kind === 'asset' ? pkg.data?.urls?.ts?.['vod'] : undefined;
  const src = asTs && tsSrc ? tsSrc : sel?.src;
  // colours / skin / theme go to the running player on every change; anything else re-creates it after a short pause
  const look: PlayerLook = lookOf(config);
  const structKey = useDebounced(structuralKey(config), 400);
  const doc = React.useMemo(() => config, [structKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const autoplay = config.controls.autoplay_muted;
  const kinds: { v: Kind; label: string }[] = [{ v: 'vod', label: t`Video` }, { v: 'channel', label: t`Live` }, { v: 'catchup', label: t`Catch-up` }];
  const empty = kind === 'vod' ? t`No ready videos in the library yet. Upload one to preview videos.` : kind === 'channel' ? t`No live channels yet.` : t`No channel records catch-up yet.`;
  const changeHref = kind === 'vod' || !sel?.channelId ? { to: '/t/$slug/delivery', params: { slug }, search: { tab: 'packaging' } } : { to: '/t/$slug/live/$channelId', params: { slug, channelId: sel.channelId }, search: { tab: 'settings' } };

  return (
    <aside aria-labelledby="pv-h" className="sticky top-0 z-20 order-first -mx-3 border-b border-border bg-bg px-3 pb-3 pt-2 md:-mx-6 md:px-6 lg:top-4 lg:order-none lg:mx-0 lg:max-h-[calc(100dvh-2rem)] lg:overflow-y-auto lg:rounded-lg lg:border lg:bg-surface lg:p-4" data-testid="pcfg-preview">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 id="pv-h" className="flex items-center gap-2 font-semibold">
          {t`Preview`}
          <span className="hidden text-xs font-normal text-fg-muted sm:inline">{t`updates as you edit`}</span>
        </h2>
        <div role="radiogroup" aria-label={t`Preview width`} className="hidden rounded-md border border-border p-0.5 sm:inline-flex" data-testid="pv-width">
          {([['desktop', t`Desktop`, Monitor], ['mobile', t`Phone`, Smartphone]] as const).map(([v, label, Icon]) => (
            <button key={v} type="button" role="radio" aria-checked={width === v} aria-label={label} title={label} onClick={() => setWidth(v)}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs focus-visible:outline-2 focus-visible:outline-focus ${width === v ? 'bg-primary text-primary-fg' : 'text-fg-muted hover:text-fg'}`}>
              <Icon className="size-4" aria-hidden /><span className="hidden xl:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div role="radiogroup" aria-label={t`What to play`} className="inline-flex rounded-md border border-border bg-surface-2 p-0.5" data-testid="pv-kind">
          {kinds.map(({ v, label }) => (
            <button key={v} type="button" role="radio" aria-checked={kind === v} onClick={() => { setKind(v); setAsTs(false); }} data-kind={v}
              className={`rounded px-2.5 py-1 text-sm focus-visible:outline-2 focus-visible:outline-focus ${kind === v ? 'bg-surface font-medium text-fg shadow-card' : 'text-fg-muted hover:text-fg'}`}>{label}</button>
          ))}
        </div>
        {options.length > 1 ? (
          <Select ariaLabel={kind === 'vod' ? t`Which video` : t`Which channel`} value={id} onValueChange={setId} className="h-9 min-w-0 max-w-full flex-1 sm:max-w-64" options={options.map((o) => ({ value: o.value, label: o.label }))} />
        ) : sel ? <span className="truncate text-sm text-fg-muted">{sel.label}</span> : null}
      </div>
      <div className={width === 'mobile' ? 'mx-auto max-w-[360px] rounded-[1.25rem] border-4 border-fg/80 p-1' : undefined} data-width={width}>
        {sel && src ? (
          // the player host's preview page (iframe): video ads reach Google IMA / Ad Manager + the tenant's allowed ad
          // domains under that page's CSP; Studio's own CSP stays strict (2026-09-30)
          <PreviewFrame key={`${src}|${version}`} tenant={slug} src={src} config={configName} configDoc={doc as unknown as Record<string, unknown>} look={look} lang={config.lang}
            autoplay={autoplay} muted={autoplay} startAtBeginning={kind === 'catchup'} label={t`Player preview`} ads />
        ) : (
          <div className="grid aspect-video place-items-center rounded-md bg-surface-2 p-4 text-center text-sm text-fg-muted" role="status">{loading ? t`Loading…` : empty}</div>
        )}
      </div>
      {sel ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid="pv-packaging">
          {pkg.data ? (
            <>
              <span>{t`Packaging`}: <strong className="ltr-isolate">{packagingLabel(pkg.data.mode)}</strong></span>
              {tsSrc ? (
                <label className="inline-flex items-center gap-1.5">
                  <Switch checked={asTs} onCheckedChange={setAsTs} aria-label={t`Play the MPEG-TS version`} className="scale-75" />
                  {t`Play the MPEG-TS version`}
                </label>
              ) : null}
              <Link to={changeHref.to as never} params={changeHref.params as never} search={changeHref.search as never} className="text-primary underline-offset-4 hover:underline">{kind === 'vod' ? t`Change for the account` : t`Change for this channel`}</Link>
            </>
          ) : null}
          <span className="hidden text-fg-muted lg:inline">{t`Saved changes reach every player within a minute.`}</span>
        </div>
      ) : null}
    </aside>
  );
}

function EmbedSection({ config, configName }: { config: PlayerConfigDoc; configName: string }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale } = useLocale();
  const toast = useToast();
  const [mode] = useUiMode();
  const [kind, setKind] = React.useState<Kind>('channel');
  const { options } = useSources(kind);
  const [id, setId] = React.useState('');
  React.useEffect(() => { if (!options.find((o) => o.value === id)) setId(options[0]?.value ?? ''); }, [options, id]);
  const sel = options.find((o) => o.value === id);
  const lang = config.lang || (locale === 'he' ? 'he' : 'en');
  const code = sel ? buildEmbed(sel.src, { skin: config.skin, autoplay: config.controls.autoplay_muted, title: sel.title, lang, tenant: tenant.slug, config: configName }) : '';
  const [showCode, setShowCode] = React.useState(false);
  return (
    <section aria-labelledby="emb-h" className="space-y-3 rounded-lg border border-border bg-surface p-4">
      <h2 id="emb-h" className="text-lg font-semibold">{t`Embed and share`}</h2>
      <p className="text-sm text-fg-muted">{t`Links and code for putting this player on a site. They use the saved settings.`}</p>
      <div className="flex flex-wrap items-end gap-3">
        <Field id="emb-kind" label={t`What to embed`}>
          <Select ariaLabel={t`What to embed`} value={kind} onValueChange={(v) => setKind(v as Kind)} options={[{ value: 'channel', label: t`Live channel` }, { value: 'vod', label: t`Video from the library` }]} />
        </Field>
        {options.length ? (
          <Field id="emb-item" label={kind === 'vod' ? t`Which video` : t`Which channel`}>
            <Select ariaLabel={kind === 'vod' ? t`Which video` : t`Which channel`} value={id} onValueChange={setId} options={options.map((o) => ({ value: o.value, label: o.label }))} />
          </Field>
        ) : <p className="mb-4 text-sm text-fg-muted">{t`Nothing to embed yet.`}</p>}
      </div>
      {sel ? <EmbedShare key={`${sel.value}|${configName}`} tenant={tenant.slug} kind={sel.hosted.kind} id={sel.hosted.id} src={sel.src} title={sel.title} idPrefix="players-es" config={configName} /> : null}
      {mode === 'advanced' ? (
        <>
          <label className="flex items-center gap-2 text-sm"><Switch checked={showCode} onCheckedChange={setShowCode} aria-label={t`Show the player-tag code`} /> {t`Show the player-tag code`}</label>
          {showCode && code ? (
            <div className="space-y-2">
              <textarea readOnly value={code} rows={4} aria-label={t`Embed code`} className="w-full rounded border border-border bg-surface-2 p-2 font-mono text-xs ltr-isolate" data-testid="embed-code" />
              <Button size="sm" onClick={() => { void navigator.clipboard?.writeText(code); toast.push({ title: t`Embed code copied`, tone: 'ok' }); }}><Copy className="size-4" aria-hidden /> {t`Copy`}</Button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
